import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  alpha,
} from '@mui/material';
import { MoreVert, Edit, Delete, ExpandLess, ExpandMore, Palette } from '@mui/icons-material';
import { TakeoffAssembly } from '../../types/AssemblyItemType';

interface TakeoffRowContextMenuProps {
  assembly: TakeoffAssembly;
  isExpanded: boolean;
  onEdit: (assembly: TakeoffAssembly) => void;
  onEditStyles: (assembly: TakeoffAssembly) => void;
  onDelete: (assembly: TakeoffAssembly) => void;
  onToggleExpand: (assemblyId: string) => void;
  theme: any;
}

export const TakeoffRowContextMenu: React.FC<TakeoffRowContextMenuProps> = ({
  assembly,
  isExpanded,
  onEdit,
  onEditStyles,
  onDelete,
  onToggleExpand,
  theme,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menuOptions = {
    labeleditAssembly: 'Edit assembly',
    labeldeleteAssembly: 'Delete assembly',
    labeltoggleExpand: isExpanded ? 'Collapse details' : 'Expand details',
    labelEditStyles: 'Edit styles',
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    // Get button position
    const button = buttonRef.current;
    if (button) {
      try {
        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate menu position to ensure it stays in view
        let x = rect.right + 8; // Add small offset from button
        let y = rect.top;

        // For very small screens, position menu below the button
        if (viewportWidth < 400) {
          x = rect.left;
          y = rect.bottom + 8;
        } else {
          // Check if menu would go off the right edge
          if (x + 180 > viewportWidth) {
            // 180px is approximate menu width
            x = rect.left - 180;
          }

          // Check if menu would go off the bottom edge
          if (y + 150 > viewportHeight) {
            // 150px is approximate menu height
            y = rect.bottom - 150;
          }

          // Check if menu would go off the left edge
          if (x < 8) {
            x = 8;
          }

          // Check if menu would go off the top edge
          if (y < 8) {
            y = 8;
          }
        }

        // Ensure menu doesn't go off the edges
        x = Math.max(8, Math.min(x, viewportWidth - 188)); // 180 + 8 for safety margin
        y = Math.max(8, Math.min(y, viewportHeight - 158)); // 150 + 8 for safety margin

        setMenuPosition({ x, y });
      } catch (error) {
        // Fallback to default positioning if there's an error
        console.warn('Error calculating menu position, using fallback:', error);
        setMenuPosition(null);
      }
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuPosition(null);
  };

  const handleEdit = () => {
    handleClose();
    onEdit(assembly);
  };

  const handleEditStyles = () => {
    handleClose();
    onEditStyles(assembly);
  };

  const handleDelete = () => {
    handleClose();
    onDelete(assembly);
  };

  const handleToggleExpand = () => {
    handleClose();
    onToggleExpand(assembly.assemblyid);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Tooltip title="More actions" arrow placement="left">
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          size="small"
          aria-label="More actions"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          data-testid="more-actions-button"
          sx={{
            padding: theme.spacing(0.25),
            color: theme.palette.text.secondary,
            minWidth: 28,
            height: 28,
            borderRadius: 0.5,
            opacity: 0.7,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.2),
              color: theme.palette.text.primary,
              opacity: 1,
            },
            transition: 'all 0.2s ease-in-out',
            '&:focus': {
              outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
              outlineOffset: 2,
              opacity: 1,
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorReference={menuPosition ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menuPosition ? { top: menuPosition.y, left: menuPosition.x } : undefined}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        disableScrollLock={false}
        keepMounted={false}
        role="menu"
        aria-label="Assembly actions"
        data-testid="assembly-actions-menu"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: theme.shadows[8],
            minWidth: 140,
            maxWidth: 180,
            zIndex: 1300,
            borderRadius: 1,
            overflow: 'hidden',
            '& .MuiMenuItem-root': {
              py: 0.75,
              px: 1.5,
              fontSize: '0.875rem',
              borderRadius: 0.5,
              margin: 0.25,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
              },
              '&:first-of-type': {
                marginTop: 0.25,
              },
              '&:last-of-type': {
                marginBottom: 0.25,
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 28,
            },
          },
        }}
      >
        <MenuItem
          onClick={handleToggleExpand}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          data-testid="toggle-expand-menu-item"
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <ListItemIcon>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {isExpanded ? menuOptions.labeltoggleExpand : menuOptions.labeltoggleExpand}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleEdit}
          aria-label={menuOptions.labeleditAssembly}
          data-testid="edit-assembly-menu-item"
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{menuOptions.labeleditAssembly}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleEditStyles}
          aria-label={menuOptions.labelEditStyles}
          data-testid="edit-styles-menu-item"
          sx={{
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <ListItemIcon>
            <Palette fontSize="small" />
          </ListItemIcon>
          <ListItemText>{menuOptions.labelEditStyles}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleDelete}
          aria-label={menuOptions.labeldeleteAssembly}
          sx={{
            color: theme.palette.error.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.05),
            },
          }}
          data-testid="delete-assembly-menu-item"
        >
          <ListItemIcon sx={{ color: theme.palette.error.main }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>{menuOptions.labeldeleteAssembly}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
